// =====================================================================================
// INVRT Radar — web console — the quote/approve card
//
// One component renders every metered decision in this console — classic mode and the
// chat panel both mount THIS card, so the spend gate cannot drift between surfaces.
//
// What it guarantees:
//   - The exact credit figure the server quoted is named before anything is spent.
//   - The current balance and the projected balance after are both on screen.
//   - Approving takes a deliberate click on a button that says what it will cost.
//   - A failed attempt keeps the quote and offers the SAME attempt again, so a retry
//     replays under one idempotency key rather than charging twice.
// =====================================================================================

import { Link } from "react-router-dom";
import { Check, Clock, X } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { ErrorState } from "./States";
import { formatCredits, formatCreditsWithUnit, formatRelative, pluralise } from "@/lib/format";
import { isLessThan, subtractExact } from "@/lib/decimal";
import type { Numeric, QuoteResponse } from "@/lib/types";

export interface QuoteApproveCardProps {
  quote: QuoteResponse;
  /** The balance as last read from Postgres. Null whilst it is still loading. */
  balance: Numeric | null;
  /** True whilst the paid call is in flight. */
  approving: boolean;
  onApprove: () => void;
  onCancel: () => void;
  /** A failed attempt. The quote survives so the same approval can be retried. */
  error?: unknown;
}

export function QuoteApproveCard({
  quote,
  balance,
  approving,
  onApprove,
  onCancel,
  error,
}: QuoteApproveCardProps) {
  // Exact decimal arithmetic, never floating point — see src/lib/decimal.ts for why this
  // one projection is computed at all, and why the figure is labelled as a projection.
  const projected = balance === null ? null : subtractExact(balance, quote.credits);
  const short = balance === null ? false : isLessThan(balance, quote.credits) === true;

  return (
    <Card className="border-accent-200">
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <CardTitle>Approve this reveal</CardTitle>
          <p className="text-sm text-ink-muted">
            Nothing has been charged yet. Credits move only when you approve.
          </p>
        </div>
        <Badge tone="info" className="shrink-0">
          <Clock className="h-3 w-3" aria-hidden="true" />
          Expires {formatRelative(quote.expires_at)}
        </Badge>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <div>
            <dt className="text-xs uppercase tracking-wide text-ink-muted">Contacts</dt>
            <dd className="text-lg font-semibold text-ink">{quote.unit_count}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-ink-muted">Cost</dt>
            <dd className="text-lg font-semibold text-accent">
              {formatCreditsWithUnit(quote.credits)}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-ink-muted">Balance now</dt>
            <dd className="text-lg font-semibold text-ink">{formatCredits(balance)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-ink-muted">
              Balance after (projected)
            </dt>
            <dd className={`text-lg font-semibold ${short ? "text-danger" : "text-ink"}`}>
              {projected === null ? "—" : formatCredits(projected)}
            </dd>
          </div>
        </dl>

        {short ? (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">
            This costs more than your balance covers. Approving will be refused and nothing
            will be charged — reduce the selection, or{" "}
            <Link
              to="/account?tab=credits"
              className="font-medium text-danger underline underline-offset-2"
            >
              top up credits
            </Link>
            .
          </p>
        ) : null}

        <p className="text-xs text-ink-muted">
          The figure above is the price Radar has already resolved and committed to for
          these exact {pluralise(quote.unit_count, "contact")}. Your balance after is
          confirmed by the ledger the moment the reveal completes.
        </p>

        {error ? <ErrorState error={error} /> : null}
      </CardContent>

      <CardFooter className="justify-end">
        <Button variant="secondary" onClick={onCancel} disabled={approving}>
          <X className="h-4 w-4" aria-hidden="true" />
          Cancel
        </Button>
        <Button variant="commit" onClick={onApprove} loading={approving}>
          <Check className="h-4 w-4" aria-hidden="true" />
          {error ? "Try this reveal again" : `Approve and spend ${formatCreditsWithUnit(quote.credits)}`}
        </Button>
      </CardFooter>
    </Card>
  );
}
